import { useState, useEffect, ChangeEventHandler } from 'react';
import type { RefObject, Dispatch, SetStateAction } from 'react';
import { passwordCheck } from 'utils/signRegex';

type PasswordCheckType =
	| 'pass'
	| 'too_short'
	| 'too_long'
	| 'wrong'
	| 'second_none'
	| 'none';

/** 회원가입 - 계정설정에서 패스워드 input의 상태를 체크하는 커스텀 훅
 *
 * @param input_ref input창의 ref
 * @returns 패스워드 / set패스워드 / helperText의 상태 / set helperText의 상태 / helperText
 */
const usePasswordCheck = <T extends HTMLElement>(
	input_ref: RefObject<T>,
): [
	string,
	PasswordCheckType,
	Record<PasswordCheckType, string>,
	(password?: string) => void,
	ChangeEventHandler<HTMLInputElement>,
] => {
	// 패스워드의 value
	const [password, setPassword] = useState('');

	// 패스워드 input의 상태 (helper text의 상태)
	const [password_check, setPasswordCheck] = useState<PasswordCheckType>('none');

	/** 패스워드의 상태에 따라 helper를 체크 */
	const passwordStateCheck = (password?: string) => {
		if (password === undefined) return;
		if (password === '') setPasswordCheck('second_none');
		else if (password.length < 8) setPasswordCheck('too_short');
		else if (!passwordCheck(password)) setPasswordCheck('wrong');
		else setPasswordCheck('pass');
	};

	/** 포커스 아웃될 경우 상태가 변경됨 */
	useEffect(() => {
		if (!input_ref.current) return;

		const focusOutEvent = (e: FocusEvent) => {
			const target = e.currentTarget as HTMLInputElement;
			if (!target) return;

			passwordStateCheck(target.value);
		};

		input_ref.current.addEventListener('focusout', focusOutEvent);

		return () => input_ref.current?.removeEventListener('focusout', focusOutEvent);
	}, [input_ref, password, password_check]);

	const helper_text: Record<PasswordCheckType, string> = {
		none: '',
		too_long: '최대 15글자입니다. 더 이상 입력할 수 없어요.',
		second_none: '비밀번호를 입력해주세요.',
		too_short: '8글자 이상 입력해주세요.',
		wrong: '영어 대/소문자, 숫자, 특수문자를 조합해주세요',
		pass: '',
	};

	const passwordChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		const value = e.currentTarget.value;

		if (value.length > 15) {
			setPasswordCheck('too_long');
			return;
		} else if (password_check === 'too_long' && value.length <= 15)
			setPasswordCheck('none');

		setPassword(value);
	};

	return [password, password_check, helper_text, passwordStateCheck, passwordChange];
};

export default usePasswordCheck;
